<script>
import Monitor from "@/services/Monitor";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Swal from "sweetalert2";

export default {
  components: {
    vueDropzone: vue2Dropzone,
  },

  props: {
    competitionDetails: {
      type: Object,
      default: null,
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      competition_name: "",
      competition_type: 10,
      competition_code: "",
      competition_country: "",
      competition_country_code: "",
      competition_logo: "",
      competition_id: "",
      competitionTypeOptions: [
        { value: 10, text: "Domestic league (10)" },
        { value: 20, text: "Domestic cup (20)" },
      ],
      file_e: "",
      photoDeleted: false,
      tryingToEdit: false,
      dropzoneOptions: {
        url: "/",
        autoProcessQueue: false,
        addRemoveLinks: true,
        thumbnailWidth: 200,
        thumbnailMethod: "contain",
        maxFilesize: 5,
        acceptedFiles: "image/*",
        maxFiles: 1,
      },
    };
  },

  watch: {
    competitionDetails: {
      immediate: true,
      handler(item) {
        if (!item || !item.competition_id) {
          return;
        }
        this.competition_id = item.competition_id || "";
        this.competition_name = item.competition_name || "";
        this.competition_type = item.competition_type || 10;
        this.competition_code = item.competition_code || "";
        this.competition_country = item.competition_country || "";
        this.competition_country_code = item.competition_country_code || "";
        this.competition_logo = item.competition_logo || "";
        this.file_e = "";
        this.photoDeleted = false;
      },
    },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("edit_sport_data_competition");
    },

    resetForm() {
      this.file_e = "";
      this.photoDeleted = false;
    },

    formatError(error) {
      if (!error) return "Failed to update competition";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    deleteImage() {
      this.photoDeleted = true;
      this.file_e = "1";
      this.competition_logo = "";
    },

    selectFile(files) {
      setTimeout(() => {
        const dropzone = this.$refs.competitionLogoDropzone;
        if (!dropzone) {
          return;
        }
        const queuedFiles = dropzone.getQueuedFiles();
        if (queuedFiles.length > 1) {
          Swal.fire("Fail!", "You can only upload one image", "warning");
          files.forEach((file) => {
            dropzone.removeFile(file);
          });
          return;
        }
        this.file_e = queuedFiles[0] || "";
        this.photoDeleted = false;
      }, 250);
    },

    async updateCompetition() {
      if (!this.competition_id) {
        return;
      }

      let logoPayload = this.file_e;
      if (!logoPayload && !this.photoDeleted && this.competition_logo) {
        logoPayload = this.competition_logo;
      }
      if (!logoPayload) {
        Swal.fire("Fail!", "Please upload a logo or provide a logo URL", "warning");
        return;
      }

      this.tryingToEdit = true;
      const payload = {
        competition_logo: logoPayload,
        provider: this.competitionDetails.provider || this.provider,
      };

      try {
        await Monitor.updateCompetitionLogo(this.competition_id, payload);
        Swal.fire("Success!", "Competition updated successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToEdit = false;
      }
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_sport_data_competition"
    title="Edit Competition"
    title-class="font-18"
    centered
    size="lg"
    @hidden="resetForm"
  >
    <form v-if="competitionDetails && competitionDetails.competition_id" @submit.prevent="updateCompetition">
      <div class="form-group mb-3">
        <label for="edit_competition_id">Competition ID</label>
        <input id="edit_competition_id" v-model="competition_id" type="text" class="form-control" readonly />
      </div>

      <div class="form-group mb-3">
        <label for="edit_competition_name">Competition Name</label>
        <input id="edit_competition_name" v-model="competition_name" type="text" class="form-control" readonly />
      </div>

      <div class="form-group mb-3">
        <label for="edit_competition_type">Competition Type</label>
        <b-form-select
          id="edit_competition_type"
          v-model="competition_type"
          :options="competitionTypeOptions"
          disabled
        />
      </div>

      <div class="form-group mb-3">
        <label for="edit_competition_code">Competition Code</label>
        <input id="edit_competition_code" v-model="competition_code" type="text" class="form-control" readonly />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="edit_competition_country">Country</label>
            <input id="edit_competition_country" v-model="competition_country" type="text" class="form-control" readonly />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="edit_competition_country_code">Country Code</label>
            <input
              id="edit_competition_country_code"
              v-model="competition_country_code"
              type="text"
              class="form-control"
              readonly
            />
          </div>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="edit_competition_logo_url">Logo URL</label>
        <input
          id="edit_competition_logo_url"
          v-model="competition_logo"
          type="text"
          class="form-control"
          :disabled="!!file_e"
          placeholder="Optional if uploading file below"
        />
      </div>

      <div class="form-group mb-0">
        <label>Competition Logo</label>
        <template v-if="!photoDeleted && competition_logo && !file_e">
          <div class="text-center mb-3">
            <img :src="competition_logo" alt="Competition Logo" width="200" class="img-thumbnail" />
          </div>
          <div class="text-center mb-3">
            <a href="javascript:void(0);" class="text-danger" title="Delete Image" @click="deleteImage">
              <i class="uil uil-trash-alt font-size-18"></i>
            </a>
          </div>
        </template>
        <template v-else>
          <vue-dropzone
            id="edit_competition_logo_dropzone"
            ref="competitionLogoDropzone"
            :use-custom-slot="true"
            :options="dropzoneOptions"
            @vdropzone-files-added="selectFile"
            no-progress-bar
          >
            <div class="dropzone-custom-content">
              <i class="display-4 text-muted bx bxs-cloud-upload"></i>
              <h4>Drop file here or click to upload.</h4>
            </div>
          </vue-dropzone>
        </template>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="updateCompetition" :disabled="tryingToEdit">
        <b-spinner v-show="tryingToEdit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
